import { useState, useMemo } from "react";
import { useSearchQuery } from "@/hooks/use-search-query";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { getArticleUrl } from "@/lib/article-url";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useBrand } from "@/contexts/brand-context";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  LayoutGrid,
  List,
  Search,
  Clock,
  Play,
  Newspaper,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  X,
  ChevronRight,
} from "lucide-react";
import type { Article } from "@shared/schema";
import { cn } from "@/lib/utils";

interface BrandTag {
  slug: string;
  name: string;
  shortName: string | null;
  color: string | null;
  source: string;
  confidence: string | null;
}

interface ArticleWithHero extends Article {
  heroImageUrl?: string | null;
  brandTags?: BrandTag[];
}

const BRAND_BADGE_COLORS: Record<string, string> = {
  "sustainable-engineering-alliance": "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  "disti": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  "devkit": "bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200",
};

type SortField = "publishedAt" | "title";
type SortDirection = "asc" | "desc";
type ViewMode = "grid" | "table";

function formatDate(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDay = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDay < 1) return "Today";
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7) return `${diffDay} days ago`;
  if (diffDay < 30) return `${Math.floor(diffDay / 7)} weeks ago`;
  return formatDate(date);
}

function ArticleGridCard({ article }: { article: ArticleWithHero }) {
  const imageUrl = article.videoThumbnail || article.heroImageUrl || article.featuredImage;
  const isVideoArticle = article.contentType === "video" || article.contentType === "mixed";
  const brandTags = (article.brandTags ?? []).filter(b => b.slug !== "fm");

  return (
    <Card className="group hover-elevate overflow-visible h-full flex flex-col" data-testid={`card-article-${article.id}`}>
      <Link href={getArticleUrl(article)} data-testid={`link-article-${article.id}`}>
        <div className="cursor-pointer h-full flex flex-col">
          <div className="aspect-[16/10] w-full overflow-hidden rounded-t-md relative bg-muted">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={article.title}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center">
                {isVideoArticle ? (
                  <Play className="h-12 w-12 text-muted-foreground" />
                ) : (
                  <Newspaper className="h-12 w-12 text-muted-foreground" />
                )}
              </div>
            )}
            {isVideoArticle && imageUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                <div className="w-12 h-12 rounded-full bg-white/90 group-hover:bg-white group-hover:scale-110 transition-all duration-300 flex items-center justify-center shadow-lg">
                  <Play className="w-6 h-6 text-slate-900 ml-0.5" fill="currentColor" />
                </div>
              </div>
            )}
          </div>
          <CardHeader className="pb-2 flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {isVideoArticle ? (
                <Badge variant="default" className="text-xs bg-red-600">
                  <Play className="h-2.5 w-2.5 mr-1" />
                  Video
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-xs">
                  News
                </Badge>
              )}
              {brandTags.map(bt => (
                <span key={bt.slug} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${BRAND_BADGE_COLORS[bt.slug] ?? "bg-muted text-muted-foreground"}`} data-testid={`badge-brand-${bt.slug}-${article.id}`}>
                  {bt.shortName ?? bt.name}
                </span>
              ))}
              {article.productType && (
                <Badge variant="outline" className="text-xs">
                  {article.productType}
                </Badge>
              )}
              {article.publishedAt && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatRelativeTime(new Date(article.publishedAt))}
                </span>
              )}
            </div>
            <CardTitle className="text-lg line-clamp-2">
              {article.title}
            </CardTitle>
            <CardDescription className="line-clamp-2 mt-2">
              {article.excerpt || "No excerpt available"}
            </CardDescription>
          </CardHeader>
        </div>
      </Link>
    </Card>
  );
}

function SortButton({
  field,
  currentField,
  direction,
  onSort,
  children,
}: {
  field: SortField;
  currentField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  children: React.ReactNode;
}) {
  const isActive = currentField === field;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 gap-1 px-2"
      onClick={() => onSort(field)}
      data-testid={`button-sort-${field}`}
    >
      {children}
      {isActive ? (
        direction === "asc" ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

export default function ArticlesArchivePage() {
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("publishedAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [contentTypeFilter, setContentTypeFilter] = useState<string>("all");
  const [productTypeFilter, setProductTypeFilter] = useState<string>("all");
  const [technologyTypeFilter, setTechnologyTypeFilter] = useState<string>("all");

  const { activeBrand, activeBrandMeta, setBrand } = useBrand();

  const { data: articles = [], isLoading } = useQuery<ArticleWithHero[]>({
    queryKey: ["/api/articles/published", activeBrand ?? "all"],
    queryFn: async () => {
      const url = activeBrand
        ? `/api/articles/published?brand=${encodeURIComponent(activeBrand)}`
        : "/api/articles/published";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch articles");
      return res.json();
    },
  });

  const uniqueProductTypes = useMemo(() => {
    const types = new Set<string>();
    articles.forEach((a) => {
      if (a.productType) types.add(a.productType);
    });
    return Array.from(types).sort();
  }, [articles]);

  const uniqueTechnologyTypes = useMemo(() => {
    const types = new Set<string>();
    articles.forEach((a) => {
      if (a.technologyType) types.add(a.technologyType);
    });
    return Array.from(types).sort();
  }, [articles]);

  const filteredAndSortedArticles = useMemo(() => {
    let result = [...articles];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(query) ||
          a.excerpt?.toLowerCase().includes(query) ||
          a.productType?.toLowerCase().includes(query) ||
          a.technologyType?.toLowerCase().includes(query)
      );
    }

    if (contentTypeFilter !== "all") {
      result = result.filter((a) => {
        if (contentTypeFilter === "video") {
          return a.contentType === "video" || a.contentType === "mixed";
        }
        return a.contentType === contentTypeFilter || (!a.contentType && contentTypeFilter === "text");
      });
    }

    if (productTypeFilter !== "all") {
      result = result.filter((a) => a.productType === productTypeFilter);
    }

    if (technologyTypeFilter !== "all") {
      result = result.filter((a) => a.technologyType === technologyTypeFilter);
    }

    result.sort((a, b) => {
      let comparison = 0;
      if (sortField === "publishedAt") {
        const dateA = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
        const dateB = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
        comparison = dateA - dateB;
      } else if (sortField === "title") {
        comparison = a.title.localeCompare(b.title);
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return result;
  }, [articles, searchQuery, sortField, sortDirection, contentTypeFilter, productTypeFilter, technologyTypeFilter]);

  // Log search queries to native analytics (fire-and-forget, skipped for authenticated users)
  useSearchQuery(searchQuery, filteredAndSortedArticles.length);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection(field === "title" ? "asc" : "desc");
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setContentTypeFilter("all");
    setProductTypeFilter("all");
    setTechnologyTypeFilter("all");
    setBrand(null);
  };

  const hasActiveFilters =
    searchQuery || contentTypeFilter !== "all" || productTypeFilter !== "all" || technologyTypeFilter !== "all" || activeBrand;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        <div className="container mx-auto px-4 py-8">
          <PageBreadcrumb items={[{ label: "Articles" }]} />
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" data-testid="text-page-title">
              Articles
            </h1>
            <p className="text-muted-foreground">
              Browse all published articles, news, and insights
            </p>
          </div>

          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="input-search"
                />
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === "grid" ? "default" : "outline"}
                  size="icon"
                  onClick={() => setViewMode("grid")}
                  data-testid="button-view-grid"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "table" ? "default" : "outline"}
                  size="icon"
                  onClick={() => setViewMode("table")}
                  data-testid="button-view-table"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {activeBrand && activeBrandMeta && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted border border-border text-sm" data-testid="status-brand-filter">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${activeBrandMeta.tailwindBg}`} />
                <span>Filtered: <span className="font-semibold">{activeBrandMeta.shortName}</span></span>
                <button onClick={() => setBrand(null)} className="text-muted-foreground hover:text-foreground" data-testid="button-dismiss-brand-filter">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Filters:</span>
              </div>

              <Select value={contentTypeFilter} onValueChange={setContentTypeFilter}>
                <SelectTrigger className="w-[140px]" data-testid="select-content-type">
                  <SelectValue placeholder="Content Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="text">Articles</SelectItem>
                  <SelectItem value="video">Videos</SelectItem>
                </SelectContent>
              </Select>

              {uniqueProductTypes.length > 0 && (
                <Select value={productTypeFilter} onValueChange={setProductTypeFilter}>
                  <SelectTrigger className="w-[160px]" data-testid="select-product-type">
                    <SelectValue placeholder="Product Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {uniqueProductTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {uniqueTechnologyTypes.length > 0 && (
                <Select value={technologyTypeFilter} onValueChange={setTechnologyTypeFilter}>
                  <SelectTrigger className="w-[160px]" data-testid="select-technology-type">
                    <SelectValue placeholder="Technology" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Technologies</SelectItem>
                    {uniqueTechnologyTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="gap-1"
                  data-testid="button-clear-filters"
                >
                  <X className="h-3 w-3" />
                  Clear
                </Button>
              )}

              <div className="ml-auto text-sm text-muted-foreground">
                {filteredAndSortedArticles.length} article{filteredAndSortedArticles.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="aspect-[16/10] w-full" />
                  <div className="p-4 space-y-3">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </Card>
              ))}
            </div>
          ) : filteredAndSortedArticles.length === 0 ? (
            <Card className="p-12 text-center">
              <Newspaper className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No articles found</h3>
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters
                  ? "Try adjusting your filters or search query"
                  : "No articles have been published yet"}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </Card>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredAndSortedArticles.map((article) => (
                <ArticleGridCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Image</TableHead>
                    <TableHead>
                      <SortButton
                        field="title"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                      >
                        Title
                      </SortButton>
                    </TableHead>
                    <TableHead className="hidden md:table-cell">Type</TableHead>
                    <TableHead className="hidden lg:table-cell">Product</TableHead>
                    <TableHead className="hidden lg:table-cell">Technology</TableHead>
                    <TableHead className="hidden sm:table-cell">
                      <SortButton
                        field="publishedAt"
                        currentField={sortField}
                        direction={sortDirection}
                        onSort={handleSort}
                      >
                        Published
                      </SortButton>
                    </TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedArticles.map((article) => {
                    const imageUrl = article.videoThumbnail || article.heroImageUrl || article.featuredImage;
                    const isVideoArticle = article.contentType === "video" || article.contentType === "mixed";
                    return (
                      <TableRow
                        key={article.id}
                        className="hover-elevate cursor-pointer"
                        data-testid={`row-article-${article.id}`}
                      >
                        <TableCell>
                          <Link href={getArticleUrl(article)}>
                            <div className="w-12 h-8 rounded overflow-hidden bg-muted flex-shrink-0">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Newspaper className="h-4 w-4 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link href={getArticleUrl(article)}>
                            <span className="font-medium hover:underline line-clamp-2">
                              {article.title}
                            </span>
                          </Link>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {isVideoArticle ? (
                            <Badge variant="default" className="text-xs bg-red-600">
                              <Play className="h-2.5 w-2.5 mr-1" />
                              Video
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">
                              Article
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {article.productType ? (
                            <Badge variant="outline" className="text-xs">
                              {article.productType}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          {article.technologyType ? (
                            <Badge variant="outline" className="text-xs">
                              {article.technologyType}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                          {article.publishedAt ? formatDate(new Date(article.publishedAt)) : "-"}
                        </TableCell>
                        <TableCell>
                          <Link href={getArticleUrl(article)}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
